import { useCallback, useEffect, useRef, useState } from 'react'
import { useTheme } from 'styled-components'

import type { ImageProps } from '../../../types/ui'
import { Radio } from '../../Checkbox/Radio/Radio'
import type { ChoiceCardProps, ChoiceValue } from '../ChoiceCard.Types'

import {
  StyledButton,
  StyledCardSection,
  StyledChoice,
  StyledImageContainer,
  StyledImageWrapper,
  StyledTitleTypography,
} from './ChoiceCardVertical.Styled'

export type ChoiceCardColorVariant = 'lightBlue' | 'grey' | 'blue2'

export type ChoiceCardVerticalProps<TValue extends ChoiceValue> = {
  colorVariant: ChoiceCardColorVariant
  hasEmptyChoiceVisible?: boolean
  imageProps: ImageProps
} & ChoiceCardProps<TValue>

export const ChoiceCardVertical = <TValue extends ChoiceValue>({
  hasEmptyChoiceVisible = false,
  imageProps,
  isSelected,
  onSelect,
  value,
  title,
  colorVariant,
  ...htmlProps
}: ChoiceCardVerticalProps<TValue>) => {
  const [isButtonPressed, setIsButtonPressed] = useState(false)
  const pointerUpFrameRef = useRef<number | null>(null)
  const theme = useTheme()

  const isHighlighted = isButtonPressed || isSelected

  const handleClick = useCallback(() => {
    if (htmlProps.disabled) return
    onSelect(value)
  }, [htmlProps, onSelect, value])

  useEffect(
    () => () => {
      if (pointerUpFrameRef.current !== null) {
        cancelAnimationFrame(pointerUpFrameRef.current)
        pointerUpFrameRef.current = null
      }
    },
    []
  )

  return (
    <StyledButton
      onClick={handleClick}
      onPointerLeave={() => {
        if (!htmlProps.disabled) setIsButtonPressed(false)
      }}
      onPointerUp={() => {
        if (htmlProps.disabled) return
        if (pointerUpFrameRef.current !== null) {
          cancelAnimationFrame(pointerUpFrameRef.current)
          pointerUpFrameRef.current = null
        }
        pointerUpFrameRef.current = requestAnimationFrame(() => {
          setIsButtonPressed(false)
          pointerUpFrameRef.current = null
        })
      }}
      onPointerDown={() => {
        if (!htmlProps.disabled) setIsButtonPressed(true)
      }}
      aria-checked={isSelected}
      aria-label={title}
      role="checkbox"
      $colorVariant={colorVariant}
      $isHighlighted={isHighlighted}
      {...htmlProps}
    >
      <StyledImageWrapper
        $isHighlighted={isHighlighted}
        $colorVariant={colorVariant}
        $isDisabled={!!htmlProps.disabled}
      >
        <StyledImageContainer
          $isHighlighted={isHighlighted}
          $colorVariant={colorVariant}
        >
          <img {...imageProps} />
        </StyledImageContainer>
      </StyledImageWrapper>
      <StyledCardSection
        $isHighlighted={isHighlighted}
        $colorVariant={colorVariant}
        $isDisabled={!!htmlProps.disabled}
      >
        <StyledTitleTypography
          as="div"
          $variant="Poppins/T3 - Medium"
          $color={
            htmlProps.disabled
              ? colorVariant === 'blue2'
                ? theme.colors['N Content/Inverse']
                : theme.colors['N Content/Disabled']
              : colorVariant === 'blue2'
                ? theme.colors['N Content/Inverse']
                : theme.colors['N Content/Primary']
          }
        >
          {title}
        </StyledTitleTypography>

        <StyledChoice>
          <Radio
            isSelected={isSelected}
            isDisabled={htmlProps.disabled}
            colorVariant={colorVariant === 'blue2' ? 'inverse' : 'default'}
            unselectedVariant={
              colorVariant === 'blue2'
                ? 'chevron-forward-circle'
                : hasEmptyChoiceVisible
                  ? 'circle'
                  : 'none'
            }
          />
        </StyledChoice>
      </StyledCardSection>
    </StyledButton>
  )
}
